// Per-OEM DGX Spark model pages — the long-tail half of the GB10 cluster.
//
// WHY THESE EXIST SEPARATELY: buyers search the OEM model name, not "DGX
// Spark" — "lenovo thinkstation pgx malaysia", "asus ascent gx10 price".
// The hub page at /enterprise/dgx-spark-malaysia/ owns the head term; these
// own the model terms.
//
// ANTI-DOORWAY RULE: a model only gets a page if it has a genuinely different
// answer to "why this one". Lenovo has TPM 2.0 and Secure Boot, HP has a
// radio-free option, ASUS has the capacity ladder, Dell has the procurement
// relationship. Where a fact is not verified against a primary source it is
// stated as "confirm at quotation" rather than invented.
//
// SOURCES: NVIDIA product page and Lenovo Press ThinkStation PGX product
// guide (lp2321), both retrieved 2026-08-19. Where only secondary coverage
// exists, the claim is kept general.
//
// RULES: no stock claims, no lead times, descriptive reseller wording only. Prices:
// Except for the user-supplied ASUS GX10 offer (2026-10-14), OUR price never appears.
// Public Malaysian retail listings are quoted as
// a dated market reference (Jazz, 2026-09-12), because these pages ranked page 1 on
// "<model> price malaysia" and earned zero clicks while answering with a refusal.
// See dgxSparkMarketPrice in dgx-spark.ts — the single place to update.

import { dgxSparkMarketPrice, dgxSparkPriceAnswer } from "./dgx-spark";
import { ASUS_GX10_PRICE_ANSWER } from "./asus-gx10-offer";

export interface DgxModelPage {
  slug: string;
  brand: string;
  model: string;
  /** Value passed to the enquiry form's ?model= preselect — must match dgxSparkOems. */
  enquiryModel: string;
  /**
   * og:image only — NOT rendered in the page body.
   *
   * It was in the body until Jazz asked what an image of text buys us, which
   * was the right question: the answer is nothing. Every fact on the card is
   * already on the page as real HTML (the specs table and "Why this build"),
   * where it is crawlable, reflows on mobile, reads to a screen reader and can
   * be edited without regenerating a JPEG. Text baked into a raster has none
   * of that. A link preview genuinely needs an image file, so the card is kept
   * for that and that alone.
   */
  card: string;
  /**
   * Official manufacturer product photography, sourced from the vendor's own
   * site or press kit and normalised to a common white canvas. `photoCredit`
   * is the rights holder named in the caption.
   */
  photo: string;
  photoCredit: string;
  h1: string;
  intro: string;
  /** "Why this build" — the differentiating answer. Verified or clearly hedged. */
  differentiators: { title: string; detail: string }[];
  /** Who it actually suits, said plainly. */
  suits: string;
  faqs: { question: string; answer: string }[];
  /** Sibling model slugs for the related strip. */
  related: string[];
  /**
   * Slug of this manufacturer's own brand page under /enterprise/, where one is
   * published. Added 2026-09-19: the Dell brand page was drawing 134 impressions
   * at position 43.5 with ZERO clicks, and this page — a Dell product page, its
   * closest neighbour on the site — did not link to it at all. Brand pages get no
   * footer link, so their only inbound links are the directory card and siblings.
   *
   * Populate ONLY where brands.ts has `published: true`. Among the GB10 OEMs that
   * is Dell alone — Lenovo, ASUS, Acer and HP are `published: false`, and linking
   * to an unpublished slug produces an internal 404.
   */
  brandPage?: string;
  trademark: string;
}

// `authorisedReseller` only where Jazz has confirmed the status for that brand
// (Lenovo, 2026-10-04); the other OEM builds stay "supplies".
const COMMON_FAQ_SUPPLY = (brand: string, model: string, authorisedReseller = false) => ({
  question: `Can I buy the ${brand} ${model} in Malaysia?`,
  answer: `Yes. Anchor Sprint ${authorisedReseller ? `is an authorised ${brand} reseller and supplies` : "supplies"} the ${brand} ${model} in Malaysia${authorisedReseller ? "" : " through authorised distribution"}, quoted in Ringgit with SST treatment stated and a MyInvois e-invoice issued on purchase. Send the configuration and quantity through the enquiry form and we come back usually within two working days.`,
});

/**
 * `from` is this model's lowest PUBLICLY LISTED Malaysian retail price — a market
 * reference, never our quotation. Omit it when no public listing exists (Acer
 * Veriton GN100 is not carried by the source retailer) and the answer falls back to
 * the family range alone. Do NOT infer a figure for a model that has none: an
 * invented entry price is a false price claim, and these pages rank at position 1
 * for "<model> price malaysia" queries.
 *
 * Both figures come from `dgxSparkMarketPrice` in dgx-spark.ts — update there.
 */
const COMMON_FAQ_PRICE = (brand: string, model: string, from?: string) => ({
  question: `How much does the ${brand} ${model} cost in Malaysia?`,
  answer: from
    ? `Publicly listed Malaysian retail for the ${brand} ${model} starts around ${from}, with the DGX Spark family running to roughly ${dgxSparkMarketPrice.high} at the top storage tiers (${dgxSparkMarketPrice.source}, ${dgxSparkMarketPrice.retrieved}). Capacity is what moves the number — the compute is identical across every OEM build. We quote against your configuration rather than a shelf price, with SST treatment stated, a MyInvois e-invoice and warranty terms included. Send the capacity and quantity and we come back in Ringgit.`
    : `${dgxSparkPriceAnswer} We do not have a public listing to point at for the ${brand} ${model} specifically, so this one is quotation-only — tell us the capacity and quantity and we return a figure in Ringgit, usually within two working days.`,
});

const COMMON_FAQ_SAME = {
  question: "Is this the same hardware as the NVIDIA DGX Spark?",
  answer:
    "The compute is identical. NVIDIA fixes the GB10 Grace Blackwell Superchip, the 128 GB of coherent unified memory at 273 GB/s and the ConnectX-7 networking across every OEM build, so benchmark performance does not change with the badge. What changes is storage capacity, cooling and acoustics, chassis, security and management features, and the warranty and support relationship behind it.",
};

export const dgxSparkModels: Record<string, DgxModelPage> = {
  "lenovo-thinkstation-pgx-malaysia": {
    slug: "lenovo-thinkstation-pgx-malaysia",
    brand: "Lenovo",
    model: "ThinkStation PGX",
    enquiryModel: "ThinkStation PGX",
    card: "dgx-lenovo-thinkstation-pgx-card.jpg",
    photo: "dgx-lenovo-thinkstation-pgx-product.jpg",
    photoCredit: "Lenovo",
    h1: "Lenovo ThinkStation PGX Malaysia — GB10 workstation, quoted in RM",
    intro:
      "Lenovo's build of the NVIDIA GB10 platform, and the one to look at when the machine has to pass a security review. It carries the same GB10 superchip and 128 GB of unified memory as every other DGX Spark, with ThinkStation's security stack and procurement relationship around it. Supplied in Malaysia through authorised distribution, quoted in RM with SST stated.",
    differentiators: [
      {
        title: "Self-encrypting storage as standard",
        detail:
          "Lenovo documents both 1 TB and 4 TB NVMe M.2 configurations, both self-encrypting (AES SED). If your data classification policy requires encryption at rest on the device itself, this is the shortest route to satisfying it.",
      },
      {
        title: "TPM 2.0 and UEFI Secure Boot",
        detail:
          "Published as part of the platform, alongside NVIDIA firmware recovery and an AMI setup password. These are the boxes an institutional security questionnaire actually asks about, and they are why this build clears review faster than a consumer-oriented one.",
      },
      {
        title: "Two-node linking built in",
        detail:
          "Two QSFP ports for NVIDIA ConnectX-7, plus 10 GbE RJ-45, Wi-Fi 7 and Bluetooth 5.3. Two PGX nodes link to work with models up to 405 billion parameters — tell us at enquiry if that is the plan so the cabling is quoted with it.",
      },
      {
        title: "ThinkStation service options",
        detail:
          "Premier Support and Keep Your Drive are available on this line. Keep Your Drive matters more than it sounds when the drive is self-encrypting and holds your training data: a failed unit never leaves your premises.",
      },
    ],
    suits:
      "Universities, hospitals, government-linked agencies and any company whose procurement runs a security review before hardware is approved. If you already buy ThinkStations, this slots into the same contract and asset process.",
    faqs: [
      COMMON_FAQ_SUPPLY("Lenovo", "ThinkStation PGX", true),
      COMMON_FAQ_PRICE("Lenovo", "ThinkStation PGX", dgxSparkMarketPrice.byBrand["Lenovo ThinkStation PGX"]),
      {
        question: "What storage does the ThinkStation PGX come with?",
        answer:
          "Lenovo documents 1 TB and 4 TB NVMe M.2 configurations, both self-encrypting. Which capacities are available in Malaysia at any moment depends on the channel, so we confirm the exact SKU with your quotation rather than promise one here.",
      },
      {
        question: "Does it have TPM 2.0 and Secure Boot?",
        answer:
          "Yes. Lenovo publishes TPM 2.0, UEFI Secure Boot, NVIDIA firmware recovery and an AMI setup password for the PGX, with AES self-encrypting NVMe storage. That combination is usually what a security review is looking for.",
      },
      COMMON_FAQ_SAME,
    ],
    related: ["hp-zgx-nano-malaysia", "dell-pro-max-gb10-malaysia", "asus-ascent-gx10-malaysia"],
    trademark:
      "Lenovo, ThinkStation and ThinkShield are trademarks of Lenovo. NVIDIA, DGX, Grace, Blackwell and ConnectX are trademarks of NVIDIA Corporation. Anchor Sprint is an authorised Lenovo reseller and an authorised NVIDIA DGX Spark reseller in Malaysia; this page does not imply endorsement by Lenovo or NVIDIA Corporation.",
  },

  "hp-zgx-nano-malaysia": {
    slug: "hp-zgx-nano-malaysia",
    brand: "HP",
    model: "ZGX Nano AI Station (G1n)",
    enquiryModel: "ZGX Nano AI Station (G1n)",
    card: "dgx-hp-zgx-nano-card.jpg",
    photo: "dgx-hp-zgx-nano-product.jpg",
    photoCredit: "HP Inc.",
    h1: "HP ZGX Nano Malaysia — GB10 AI station, quoted in RM",
    intro:
      "HP's Z-series build of the NVIDIA GB10 platform. Same superchip and 128 GB of unified memory as every DGX Spark, wrapped in HP's workstation management and support model — and notable for being the build most often specified where radios are not allowed in the room. Supplied in Malaysia through authorised distribution, quoted in RM with SST stated.",
    differentiators: [
      {
        title: "A radio-free configuration exists",
        detail:
          "HP's coverage of the ZGX Nano notes a configuration without wireless radios. For secure facilities, labs and any site whose policy forbids Wi-Fi and Bluetooth in the room, that single option decides the purchase — most GB10 builds ship with Wi-Fi 7 and Bluetooth as standard. Confirm the exact SKU with us before ordering.",
        },
      {
        title: "Remote management for a fleet",
        detail:
          "HP's remote KVM approach is aimed at estates where a technician cannot be at the desk. If you are buying more than one and they will live in a lab rather than under someone's monitor, remote access is what you will miss first if you skip it.",
      },
      {
        title: "Self-encrypting storage",
        detail:
          "Available on this build, which matters for the same reason it does on the Lenovo: data at rest on the device, satisfied at the hardware level rather than by policy.",
      },
      {
        title: "Z-series support model",
        detail:
          "If your organisation already runs HP Z workstations, this arrives inside the support and asset arrangements you have, rather than as a new vendor relationship to set up.",
      },
    ],
    suits:
      "Secure facilities and labs where wireless is restricted, and organisations already standardised on HP Z workstations who want the GB10 machine to arrive inside existing support contracts.",
    faqs: [
      COMMON_FAQ_SUPPLY("HP", "ZGX Nano AI Station"),
      COMMON_FAQ_PRICE("HP", "ZGX Nano AI Station", dgxSparkMarketPrice.byBrand["HP ZGX Nano G1n"]),
      {
        question: "Can I get the HP ZGX Nano without Wi-Fi and Bluetooth?",
        answer:
          "A radio-free configuration is part of what distinguishes this build, which is why it comes up for secure sites. Availability of that specific SKU through Malaysian distribution changes, so tell us it is a requirement at enquiry and we will confirm it before you commit — we will not quote a configuration we cannot source.",
      },
      COMMON_FAQ_SAME,
    ],
    related: ["lenovo-thinkstation-pgx-malaysia", "dell-pro-max-gb10-malaysia", "asus-ascent-gx10-malaysia"],
    trademark:
      "HP, HP Z and ZGX are trademarks of HP Inc. NVIDIA, DGX, Grace, Blackwell and ConnectX are trademarks of NVIDIA Corporation. Anchor Sprint is an authorised NVIDIA DGX Spark reseller in Malaysia, sourcing this HP build through authorised Malaysian distribution; it is not affiliated with HP Inc., and this page does not imply endorsement by HP Inc. or NVIDIA Corporation.",
  },

  "asus-ascent-gx10-malaysia": {
    slug: "asus-ascent-gx10-malaysia",
    brand: "ASUS",
    model: "Ascent GX10",
    enquiryModel: "Ascent GX10",
    card: "dgx-asus-ascent-gx10-card.jpg",
    photo: "dgx-asus-ascent-gx10-product.jpg",
    photoCredit: "ASUSTeK Computer Inc.",
    h1: "ASUS Ascent GX10 Malaysia — GB10 AI supercomputer, quoted in RM",
    intro:
      "ASUS's build of the NVIDIA GB10 platform, and generally the one recommended when the buyer is spending their own money rather than a department's. Identical GB10 superchip and 128 GB of unified memory to every DGX Spark, with a capacity ladder that starts lower than most. Supplied in Malaysia through authorised distribution, quoted in RM with SST stated.",
    differentiators: [
      {
        title: "The widest capacity ladder",
        detail:
          "The Ascent GX10 is offered across more than one storage tier, reported from a 1 TB entry configuration up to a 4 TB PCIe Gen5 option. That is what lets a team start at the bottom of the range instead of paying for storage a prototype will never use. We confirm the exact SKUs available through Malaysian distribution with your quotation.",
      },
      {
        title: "Best capability per Ringgit, usually",
        detail:
          "Because the compute is fixed by NVIDIA, a lower entry configuration means the same petaFLOP for less outlay. Independent coverage consistently lands on the GX10 as the value pick for individual developers and small teams for exactly this reason.",
      },
      {
        title: "ASUS's own thermal design",
        detail:
          "Cooling and acoustics are one of the few places OEMs genuinely differentiate on GB10, and ASUS markets its thermal design as a selling point. If the machine will sit on a desk beside you rather than in a rack, this is worth asking about.",
      },
      {
        title: "Stackable chassis",
        detail:
          "Designed to sit alongside a second unit, which matters if the plan is to link two for models up to 405 billion parameters rather than buy one and reconsider later.",
      },
    ],
    suits:
      "Individual developers, research teams and startups who want maximum capability per Ringgit and do not need an enterprise security stack or a fleet management story.",
    faqs: [
      COMMON_FAQ_SUPPLY("ASUS", "Ascent GX10"),
      {
        question: "How much does the ASUS Ascent GX10 cost in Malaysia?",
        answer: ASUS_GX10_PRICE_ANSWER,
      },
      {
        question: "What storage capacities does the Ascent GX10 come in?",
        answer:
          "It is offered across more than one tier, reported from 1 TB up to a 4 TB PCIe Gen5 configuration. Which of those is actually available through Malaysian distribution changes with allocation, so we confirm the SKU with your quotation instead of promising a capacity here.",
      },
      {
        question: "Is the ASUS Ascent GX10 slower than the NVIDIA DGX Spark?",
        answer:
          "No. The GB10 superchip, the 128 GB of unified memory and the 273 GB/s bandwidth are fixed by NVIDIA across every OEM build, so sustained compute is the same. Differences show up in storage speed, thermals under long runs, and the support you get — not in the silicon.",
      },
      COMMON_FAQ_SAME,
    ],
    related: ["lenovo-thinkstation-pgx-malaysia", "hp-zgx-nano-malaysia", "dell-pro-max-gb10-malaysia"],
    trademark:
      "ASUS and Ascent are trademarks of ASUSTeK Computer Inc. NVIDIA, DGX, Grace, Blackwell and ConnectX are trademarks of NVIDIA Corporation. Anchor Sprint is an authorised NVIDIA DGX Spark reseller in Malaysia, sourcing this ASUS build through authorised Malaysian distribution; it is not affiliated with ASUSTeK, and this page does not imply endorsement by ASUSTeK or NVIDIA Corporation.",
  },

  "dell-pro-max-gb10-malaysia": {
    slug: "dell-pro-max-gb10-malaysia",
    brand: "Dell Technologies",
    model: "Pro Max with GB10",
    enquiryModel: "Pro Max with GB10",
    card: "dgx-dell-pro-max-gb10-card.jpg",
    photo: "dgx-dell-pro-max-gb10-product.jpg",
    photoCredit: "Dell Inc.",
    h1: "Dell Pro Max with GB10 Malaysia — quoted in RM",
    intro:
      "Dell's build of the NVIDIA GB10 platform. The compute is identical to every other DGX Spark; what you are buying with the Dell badge is the procurement and service relationship you already have. Supplied in Malaysia through authorised distribution, quoted in RM with SST stated and MyInvois handled.",
    differentiators: [
      {
        title: "It arrives inside your existing Dell agreement",
        detail:
          "For an organisation already buying Dell workstations and servers, this is the GB10 machine that needs no new vendor onboarding, no new service contract and no new asset process. That is a duller reason than benchmarks and it is the one that actually decides most corporate purchases.",
      },
      {
        title: "Familiar service contracts",
        detail:
          "Dell's workstation support tiers apply, which means the escalation path when something fails is the one your IT team already knows rather than one they have to learn during an outage.",
      },
      {
        title: "Storage configured to the SKU",
        detail:
          "Dell's configurations differ from the NVIDIA reference build, and the capacity offered varies by region and SKU. We confirm what is actually available through Malaysian distribution with your quotation rather than quoting a US configuration at you.",
      },
      {
        title: "Fleet-friendly",
        detail:
          "If the plan is several units across a team, buying them the same way you buy the rest of the estate keeps them inside one asset register and one refresh cycle.",
      },
    ],
    suits:
      "Companies already standardised on Dell whose procurement, service contracts and asset management would otherwise have to make an exception for a one-off vendor.",
    faqs: [
      COMMON_FAQ_SUPPLY("Dell", "Pro Max with GB10", true),
      COMMON_FAQ_PRICE("Dell", "Pro Max with GB10", dgxSparkMarketPrice.byBrand["Dell Pro Max with GB10"]),
      {
        question: "How is the Dell Pro Max with GB10 different from the NVIDIA DGX Spark?",
        answer:
          "Not in compute — the GB10 superchip, 128 GB of unified memory and ConnectX-7 networking are fixed by NVIDIA. The difference is the storage configuration, the chassis and cooling, and the support and procurement relationship. If you buy Dell already, that last one is usually worth more than any spec difference.",
      },
      COMMON_FAQ_SAME,
    ],
    related: ["lenovo-thinkstation-pgx-malaysia", "hp-zgx-nano-malaysia", "asus-ascent-gx10-malaysia"],
    brandPage: "dell-technologies-malaysia",
    trademark:
      "Dell, Dell Technologies and Pro Max are trademarks of Dell Inc. NVIDIA, DGX, Grace, Blackwell and ConnectX are trademarks of NVIDIA Corporation. Anchor Sprint is an authorised Dell reseller in Malaysia; this page does not imply endorsement by Dell. Anchor Sprint is also an authorised NVIDIA DGX Spark reseller; this does not imply endorsement by NVIDIA Corporation.",
  },

  "acer-veriton-gn100-malaysia": {
    slug: "acer-veriton-gn100-malaysia",
    brand: "Acer",
    model: "Veriton GN100 AI Mini Workstation",
    enquiryModel: "Veriton GN100 AI Mini Workstation",
    card: "dgx-acer-veriton-gn100-card.jpg",
    photo: "dgx-acer-veriton-gn100-product.jpg",
    photoCredit: "Acer Inc.",
    h1: "Acer Veriton GN100 Malaysia — GB10 mini workstation, quoted in RM",
    intro:
      "Acer's build of the NVIDIA GB10 platform, listed by NVIDIA alongside the other OEM versions. Same GB10 superchip and 128 GB of unified memory as every DGX Spark, in Acer's compact Veriton chassis. Supplied in Malaysia through authorised distribution, quoted in RM with SST stated.",
    differentiators: [
      {
        title: "The same compute, a different badge",
        detail:
          "NVIDIA fixes the superchip, the memory and the networking, so the Veriton GN100 delivers the same petaFLOP at FP4 and the same 128 GB at 273 GB/s as the reference machine. Nobody should pay a premium expecting more, and nobody should expect less.",
      },
      {
        title: "Acer's Malaysian service presence",
        detail:
          "Acer has long-standing service coverage in Malaysia. For buyers outside Klang Valley, where a vendor can actually service the unit is a more practical question than which chassis is quieter.",
      },
      {
        title: "Compact by design",
        detail:
          "Built to the same roughly one-litre footprint as the rest of the GB10 family, which is the whole point of the category — a petaFLOP that fits beside a monitor and runs off a normal wall socket.",
      },
    ],
    suits:
      "Buyers who want the GB10 platform with a vendor that has established Malaysian service coverage, without needing the enterprise security or fleet-management features the Lenovo and HP builds add.",
    faqs: [
      COMMON_FAQ_SUPPLY("Acer", "Veriton GN100"),
      COMMON_FAQ_PRICE("Acer", "Veriton GN100"),
      COMMON_FAQ_SAME,
      {
        question: "Which DGX Spark build should I actually buy?",
        answer:
          "Since the compute is identical, choose on the things that are not: pick Lenovo if a security review needs TPM 2.0 and self-encrypting storage, HP if the room forbids wireless or you need remote management, ASUS if you want the most capability per Ringgit, Dell if you already buy Dell. Tell us the constraint and we will tell you which one it points at.",
      },
    ],
    related: ["asus-ascent-gx10-malaysia", "lenovo-thinkstation-pgx-malaysia", "hp-zgx-nano-malaysia"],
    trademark:
      "Acer and Veriton are trademarks of Acer Inc. NVIDIA, DGX, Grace, Blackwell and ConnectX are trademarks of NVIDIA Corporation. Anchor Sprint is an authorised NVIDIA DGX Spark reseller in Malaysia, sourcing this Acer build through authorised Malaysian distribution; it is not affiliated with Acer Inc., and this page does not imply endorsement by Acer Inc. or NVIDIA Corporation.",
  },
};

export const dgxSparkModelSlugs = Object.keys(dgxSparkModels);
